import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { SEOService } from "../../services/seoService";

const StaticSEOTags = ({
  fallbackTitle = "Polani Travels - Your Travel Partner",
  fallbackDescription = "Book your next journey with Polani Travels. We offer the best travel packages and services.",
  fallbackKeywords = "travel packages, tours, polani",
  fallbackOgTitle = "Polani Travels - Your Travel Partner",
  fallbackOgDescription = "Book your next journey with Polani Travels. We offer the best travel packages and services.",
  fallbackOgImage = "",
  fallbackOgUrl = "",
  fallbackCanonicalUrl = "",
  fallbackAuthor = "Polani Travels",
  fallbackRobots = "index, follow",
}) => {
  const [seoSettings, setSeoSettings] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const loadSEOSettings = async () => {
      try {
        // First try to get from cache (for static generation)
        let settings = SEOService.getSEOSettingsFromCache(location.pathname);

        if (!settings) {
          // If not in cache, try to fetch from Firebase
          settings = await SEOService.getActiveSEOSettings(location.pathname);

          if (!settings) {
            // Try root path as fallback
            settings = await SEOService.getActiveSEOSettings("/");
          }
        }

        setSeoSettings(settings);
      } catch (error) {
        console.error("Error loading SEO settings:", error);
      } finally {
        setIsLoaded(true);
      }
    };

    loadSEOSettings();
  }, [location.pathname]);

  // Always render SEO tags immediately, even before loading
  // This ensures they're available for static generation
  const currentSettings = seoSettings || {};

  return (
    <Helmet prioritizeSeoTags>
      {/* Basic Meta Tags */}
      <title>{currentSettings.title || fallbackTitle}</title>
      <meta
        name="description"
        content={currentSettings.description || fallbackDescription}
      />
      <meta
        name="keywords"
        content={currentSettings.keywords || fallbackKeywords}
      />

      {/* Canonical URL */}
      {(currentSettings.canonicalUrl || fallbackCanonicalUrl) && (
        <link
          rel="canonical"
          href={currentSettings.canonicalUrl || fallbackCanonicalUrl}
        />
      )}

      {/* Robots Meta */}
      <meta name="robots" content={currentSettings.robots || fallbackRobots} />

      {/* Author */}
      {(currentSettings.author || fallbackAuthor) && (
        <meta
          name="author"
          content={currentSettings.author || fallbackAuthor}
        />
      )}

      {/* Open Graph Tags */}
      <meta
        property="og:title"
        content={
          currentSettings.ogTitle || currentSettings.title || fallbackOgTitle
        }
      />
      <meta
        property="og:description"
        content={
          currentSettings.ogDescription ||
          currentSettings.description ||
          fallbackOgDescription
        }
      />
      {(currentSettings.ogImage || fallbackOgImage) && (
        <meta
          property="og:image"
          content={currentSettings.ogImage || fallbackOgImage}
        />
      )}
      <meta property="og:type" content={currentSettings.ogType || "website"} />
      {(currentSettings.ogUrl || fallbackOgUrl) && (
        <meta
          property="og:url"
          content={currentSettings.ogUrl || fallbackOgUrl}
        />
      )}

      {/* Twitter Card Tags */}
      <meta
        name="twitter:card"
        content={currentSettings.twitterCard || "summary_large_image"}
      />
      <meta
        name="twitter:title"
        content={
          currentSettings.twitterTitle ||
          currentSettings.title ||
          fallbackOgTitle
        }
      />
      <meta
        name="twitter:description"
        content={
          currentSettings.twitterDescription ||
          currentSettings.description ||
          fallbackOgDescription
        }
      />
      {(currentSettings.twitterImage || fallbackOgImage) && (
        <meta
          name="twitter:image"
          content={currentSettings.twitterImage || fallbackOgImage}
        />
      )}

      {/* Additional Technical Tags */}
      <meta httpEquiv="Content-Type" content="text/html; charset=utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1" />
      <html lang="en" />
    </Helmet>
  );
};

export default StaticSEOTags;
